import {
  ChangeDetectionStrategy,
  Component,
  signal,
  Input,
  OnInit,
  inject,
  Injector,
} from '@angular/core';
import {
  AsyncPipe,
  CommonModule,
} from '@angular/common';
import { Router } from '@angular/router';
import {
  TuiButton,
  TuiLink,
  TuiPopup,
  TuiTitle,
  TuiIcon,
  TuiLoader,
  TuiAppearance,
} from '@taiga-ui/core';
import {
  TuiDrawer,
  TuiTooltip,
  TuiAvatar,
  TuiBadge,
  TuiStatus,
  TuiButtonClose,
} from '@taiga-ui/kit';
import { SignOutConfirmationDialog } from '../../sign-out-confirmation-dialog/sign-out-confirmation-dialog';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TuiDialogService } from '@taiga-ui/core';
import {
  TuiHeader,
  TuiCell,
  TuiCardLarge,
} from '@taiga-ui/layout';
import {
  TuiTable,
} from '@taiga-ui/addon-table';
import { TuiFormatDatePipe } from '@taiga-ui/core';
import { ApiService } from '../../../services/api.service';
import { NotificationService } from '../../../services/notification.service';
import { DeviceIdentifier } from '../../../utils/device-identifier.util';
import { EmptyState } from '../../empty-state/empty-state';

export interface DeviceData {
  id: string;
  session_id: string;
  device: {
    name: string;
    browser: string;
    ip: string;
    icon: string;
  };
  location: string;
  firstSignIn: string | Date;
  lastActivity: string | Date | null;
  isActive: boolean;
  isCurrent: boolean;
}

@Component({
  standalone: true,
  selector: 'app-devices-drawer',
  imports: [
    CommonModule,
    TuiButton,
    TuiButtonClose,
    TuiLink,
    TuiPopup,
    TuiTitle,
    TuiIcon,
    TuiDrawer,
    TuiTooltip,
    TuiAvatar,
    TuiHeader,
    TuiCell,
    TuiTable,
    TuiLoader,
    TuiBadge,
    TuiStatus,
    TuiAppearance,
    EmptyState,
  ],
  templateUrl: './devices-drawer.html',
  styleUrl: './devices-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesDrawer implements OnInit {
  private readonly injector = inject(Injector);
  private readonly apiService = inject(ApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly dialogs = inject(TuiDialogService);

  protected readonly open = signal(false);
  protected readonly loading = signal(false);
  protected readonly targetUserId = signal<string | null>(null);
  protected readonly activeSessions = signal<DeviceData[]>([]);


  protected readonly deviceColumns = ['device', 'location', 'last-activity', 'action'] as const;

  ngOnInit(): void {
   // Initial fetch if drawer is used independently
    if (this.open()) {
      this.fetchSessions();
    }
  }

  public async openDrawer(userId?: string): Promise<void> {
    this.targetUserId.set(userId || null);
    this.open.set(true);
    this.fetchSessions();
  }

  public closeDrawer(): void {
    this.open.set(false);
  }

  private fetchSessions(): void {
    this.loading.set(true);
    const userId = this.targetUserId() || this.apiService.getCurrentUser()?.id || '';
    this.apiService.getUserSessions(userId).subscribe({
      next: (response) => {
        const sessions = Array.isArray(response) ? response : (response?.data || []);
        this.activeSessions.set(sessions.map((s: any) => this.mapSessionToDeviceData(s)));
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to fetch sessions:', err);
        this.loading.set(false);
        this.notificationService.showError('Failed to load active sessions');
      }
    });
  }

  private mapSessionToDeviceData(session: any): DeviceData {
   // Parse location string: "Surat-Gujarat-India-395007 | ..." -> "Surat, India"
    let locationStr = 'Unknown Location';
    if (session.location) {
      const parts = session.location.split('|')[0].split('-');
      if (parts.length >= 3) {
        locationStr = `${parts[0]}, ${parts[2]}`;
      } else {
        locationStr = session.location.split('|')[0].trim();
      }
    }

    const deviceInfo = DeviceIdentifier.parseSessionDeviceInfo(session);

    const currentUser = this.apiService.getCurrentUser();
    const currentSessionId = currentUser?.session_id || currentUser?.sessionId;
    const currentDeviceInfo = this.apiService.getDeviceInfo();

   // Browser/OS names for current environment
    const currentBrowserName = currentDeviceInfo.browser.name;
    const currentOsName = currentDeviceInfo.os.name;

   // Identifying logic:
    let isCurrent = session.id === currentSessionId || session.session_id === currentSessionId || !!session.is_current || !!session.current;

    if (!isCurrent && !currentSessionId) {
      isCurrent = deviceInfo.browser?.name === currentBrowserName && deviceInfo.os?.name === currentOsName;
    }

    let deviceName = DeviceIdentifier.getBestSessionDeviceName(session, deviceInfo);

   // For the current device, we often have a resolved name in userInfo
    if (isCurrent && currentUser?.device_name_clean) {
      deviceName =
        DeviceIdentifier.getSafeReadableDeviceName(currentUser.device_name_clean, currentDeviceInfo) ||
        deviceName;
    }

   // Determine icon dynamically using DeviceIdentifier
    const icon = DeviceIdentifier.getDeviceIcon(session, deviceInfo, deviceName);

    return {
      id: session.id,
      session_id: session.session_id || session.id,
      device: {
        name: deviceName,
        browser: DeviceIdentifier.getSessionBrowserLabel(session, deviceInfo),
        ip: session.ip_address || '-',
        icon: icon
      },
      location: locationStr,
      firstSignIn: session.createdAt,
      lastActivity: session.last_activity_at || session.updatedAt,
      isActive: session.is_active,
      isCurrent: isCurrent
    };
  }

  private capitalize(str: string): string {
    if (!str || str === 'unknown') return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  public logoutDevice(session: DeviceData): void {
    this.dialogs
      .open<boolean>(new PolymorpheusComponent(SignOutConfirmationDialog, this.injector), {
        data: { session },
        size: 's',
        dismissible: true,
      })
      .subscribe(confirmed => {
        if (confirmed) {
        const userId = this.targetUserId() || this.apiService.getCurrentUser()?.id;
        if (!userId) {
          this.notificationService.showError('User session not found');
          return;
        }

        if (session.isCurrent) {
          this.apiService.logout().subscribe({
            next: () => {
              this.notificationService.showSuccess('Logged out successfully');
              this.router.navigate(['/login']);
            },
            error: (err) => {
              console.error('Logout failed:', err);
              this.router.navigate(['/login']);
            }
          });
          return;
        }

        this.apiService.logoutUserSession(userId, session.session_id).subscribe({
          next: () => {
            this.notificationService.showSuccess('Device logged out successfully');
            this.fetchSessions();
          },
          error: (err) => {
            console.error('Logout failed:', err);
            this.notificationService.showError('Failed to logout device');
          }
        });
      }
    });
  }

  public logoutAllSessions(): void {
    const userId = this.targetUserId() || this.apiService.getCurrentUser()?.id;
    if (!userId) {
      this.notificationService.showError('User session not found');
      return;
    }

    this.apiService.logoutAllUserSessions(userId).subscribe({
      next: () => {
        this.notificationService.showSuccess('All sessions logged out successfully');
        this.fetchSessions();
      },
      error: (err) => {
        console.error('Logout all failed:', err);
        this.notificationService.showError('Failed to logout all sessions');
      }
    });
  }
}
